import React, { useEffect, useMemo, useState } from 'react';
import { geeService } from '../../services/geeService';
import gisData from '../../data/gisAnalysis.json';

const API = process.env.REACT_APP_API_BASE_URL || '';

const C = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed', orange: '#d97706', red: '#dc2626',
  text: '#1e293b', muted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#fff',
};

const fmt = (n, d = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: d });
};

const th = {
  textAlign: 'left', padding: '9px 11px', background: C.bg, borderBottom: `2px solid ${C.border}`,
  fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
};
const td = { padding: '8px 11px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text };

const IP3_STEPS = [
  { id: '3.1', title: '3.1 Yüksek çözünürlüklü uydu görüntülerinin çıkarılması', desc: 'Yeşil alan koordinatları ile Sentinel-2 görüntüleri' },
  { id: '3.2', title: '3.2 Segmentasyon — yeşil / yapısal alan sınırları', desc: 'NDVI / NDBI / NDWI ile örtü sınıfları' },
  { id: '3.3', title: '3.3 Segmentasyon — bitki örtüsü / tipleri', desc: 'Red Edge + SWIR tipoloji sınıflandırması' },
  { id: '3.4', title: '3.4 Sahadaki veriler ile çapraz kontrol', desc: 'Uydu çıktısı ↔ ASKİ / doğrulama / GIS alan karşılaştırması' },
  { id: '3.5', title: '3.5 Vektörel veritabanı ve CBS entegrasyonu', desc: 'Öznitelikli yeşil alan katmanı' },
  { id: 'C.3.1', title: 'Ç.3.1 Zenginleştirilmiş kentsel yeşil alan veritabanı', desc: 'CBS üzerinde entegre öznitelik tablosu' },
];

/** Merkez noktadan yaklaşık kare poligon (lon,lat) — metre cinsinden yarı çap */
function bufferSquare(lng, lat, meters = 90) {
  const dLat = meters / 111320;
  const dLng = meters / (111320 * Math.cos((lat * Math.PI) / 180));
  return [
    [lng - dLng, lat - dLat],
    [lng + dLng, lat - dLat],
    [lng + dLng, lat + dLat],
    [lng - dLng, lat + dLat],
    [lng - dLng, lat - dLat],
  ];
}

function parseBoundaries(raw) {
  if (!raw) return null;
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(v) && v.length >= 3) {
      // [[lat,lng]] or [[lng,lat]] — GEE wants [lng,lat]
      const first = v[0];
      if (Array.isArray(first) && first.length >= 2) {
        // Heuristic: Ankara lat ~39, lng ~32
        if (Math.abs(first[0]) > 30 && Math.abs(first[0]) < 45 && Math.abs(first[1]) > 20) {
          // likely [lat, lng]
          return v.map((p) => [p[1], p[0]]);
        }
        return v.map((p) => [p[0], p[1]]);
      }
    }
  } catch { /* ignore */ }
  return null;
}

function bboxFromCoords(coords) {
  if (!coords?.length) return null;
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return {
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
}

function findGisMatch(aboneno, aboneAdi) {
  const matched = gisData?.matchExcel?.matched || [];
  const byNo = matched.find((m) => String(m.aboneno) === String(aboneno));
  if (byNo) return byNo;
  const name = (aboneAdi || '').toLowerCase();
  return matched.find((m) => (m.askiName || '').toLowerCase() === name) || null;
}

const Section = ({ step, children }) => (
  <div style={{ marginBottom: 28, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: 'linear-gradient(90deg,#f0fdf4,#f8fafc)' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{step.title}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{step.desc}</div>
    </div>
    <div style={{ padding: 14 }}>{children}</div>
  </div>
);

const AdminRaporIP3 = ({ parks = [] }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geeStatus, setGeeStatus] = useState('idle');
  const [geeError, setGeeError] = useState(null);
  const [running, setRunning] = useState(false);
  const [analyses, setAnalyses] = useState({}); // aboneno -> { ndvi, detection, typology }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);

        // Öncelik: Rapor’daki aynı 5 park (önceden hesaplı konum + doğrulama)
        const fromParks = (parks || []).slice(0, 5).map((p) => {
          const loc = p.location || {};
          let lat = loc.lat != null ? parseFloat(loc.lat) : null;
          let lng = loc.lng != null ? parseFloat(loc.lng) : null;
          let coords = loc.coords || null;
          let coordSource = loc.coordSource || null;
          if (!coords && lat != null && lng != null) {
            coords = bufferSquare(lng, lat, 90);
            coordSource = coordSource || 'Merkez ±90 m kare buffer';
          }
          const gis = findGisMatch(p.aboneno, p.abone_adi);
          return {
            aboneno: String(p.aboneno || ''),
            abone_adi: p.abone_adi || '-',
            ilce_adi: p.ilce_adi || '-',
            mahalle_adi: p.mahalle_adi || '-',
            sokak_adi: p.sokak_adi || null,
            lat, lng,
            coords,
            coordSource: coords ? coordSource : 'GPS yok (doğrulama/DB’de yok)',
            vertexCount: coords ? coords.length : 0,
            status: loc.verificationStatus || p.kaynak || 'aski',
            db_park_id: loc.db_park_id || loc.dbPark?.park_id || null,
            gisAlan: gis?.alan ?? null,
            gisName: gis?.gisName || null,
            gisScore: gis?.score ?? null,
            askiTuketim: p.stats?.mean ?? null,
            askiDonem: p.series?.[p.series.length - 1]?.donem ?? null,
          };
        });

        // Eksik GPS’leri canlı doğrulamadan tamamla
        const need = fromParks.filter((s) => !s.coords);
        if (need.length) {
          const verRes = await fetch(`${API}/api/park-verification/parks?status=verified&limit=50`)
            .then((r) => r.json())
            .catch(() => ({ parks: [] }));
          const byNo = {};
          (verRes.parks || []).forEach((p) => {
            const no = String(p.aski?.aboneno || '');
            if (no) byNo[no] = p;
          });
          need.forEach((s) => {
            const hit = byNo[s.aboneno];
            if (!hit) return;
            const v = hit.verification || {};
            const lat = v.center_lat != null ? parseFloat(v.center_lat) : null;
            const lng = v.center_lng != null ? parseFloat(v.center_lng) : null;
            let coords = parseBoundaries(v.boundaries);
            let coordSource = coords ? 'Doğrulama poligonu' : null;
            if (!coords && lat && lng) {
              coords = bufferSquare(lng, lat, 90);
              coordSource = 'Doğrulama merkez ±90 m';
            }
            if (coords) {
              s.lat = lat;
              s.lng = lng;
              s.coords = coords;
              s.coordSource = coordSource;
              s.vertexCount = coords.length;
              s.status = hit.status || v.status || s.status;
              s.db_park_id = v.db_park_id || hit.dbPark?.park_id || s.db_park_id;
            }
          });
        }

        if (!cancelled) setSites(fromParks);
      } catch (e) {
        if (!cancelled) setGeeError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [parks]);

  const initGee = async () => {
    try {
      setGeeStatus('loading');
      setGeeError(null);
      const projectId = process.env.REACT_APP_GEE_PROJECT || '';
      if (projectId) await geeService.initGEEWithProject(projectId);
      else await geeService.initializeGEE();
      setGeeStatus('ready');
    } catch (e) {
      setGeeStatus('error');
      setGeeError(e.message || 'GEE başlatılamadı');
    }
  };

  const runSatelliteAnalyses = async () => {
    const runnable = sites.filter((s) => s.coords?.length >= 4);
    if (!runnable.length) {
      setGeeError('Analiz için koordinatı olan park yok.');
      return;
    }
    if (geeStatus !== 'ready') {
      await initGee();
    }
    if (!geeService.isGEEInitialized?.() && geeStatus !== 'ready') {
      // wait for state — re-check
      try {
        const projectId = process.env.REACT_APP_GEE_PROJECT || '';
        if (projectId) await geeService.initGEEWithProject(projectId);
        else await geeService.initializeGEE();
        setGeeStatus('ready');
      } catch (e) {
        setGeeError(e.message);
        return;
      }
    }

    setRunning(true);
    setGeeError(null);
    const next = { ...analyses };
    const dateStart = '2024-04-01';
    const dateEnd = '2024-10-31';

    for (const site of runnable) {
      try {
        const [ndvi, detection, typology] = await Promise.all([
          geeService.computeNDVI(site.coords, dateStart, dateEnd, 40, 0.35),
          geeService.computeGreenDetection(site.coords, dateStart, dateEnd, 40),
          geeService.computeTypology(site.coords, dateStart, dateEnd, 40),
        ]);
        next[site.aboneno] = { ndvi, detection, typology, ok: true, error: null };
      } catch (e) {
        next[site.aboneno] = { ok: false, error: e.message || 'Analiz hatası' };
      }
      setAnalyses({ ...next });
    }
    setRunning(false);
  };

  const extractRows = useMemo(() => sites.map((s) => {
    const bb = bboxFromCoords(s.coords);
    return {
      ...s,
      bbox: bb,
      resolution: s.coords ? '10 m (Sentinel-2 SR)' : '-',
      sensor: s.coords ? 'COPERNICUS/S2_SR' : '-',
    };
  }), [sites]);

  const crossRows = useMemo(() => sites.map((s) => {
    const a = analyses[s.aboneno];
    const satGreen = a?.detection?.totalGreenM2 ?? a?.ndvi?.greenAreaM2 ?? null;
    const satTotal = a?.detection?.totalAreaM2 ?? a?.ndvi?.totalAreaM2 ?? null;
    const gisAlan = s.gisAlan != null ? Number(s.gisAlan) : null;
    let diffPct = null;
    if (satGreen != null && gisAlan != null && gisAlan > 0) {
      diffPct = ((satGreen - gisAlan) / gisAlan) * 100;
    }
    return {
      aboneno: s.aboneno,
      abone_adi: s.abone_adi,
      satGreen,
      satTotal,
      ndviMean: a?.ndvi?.ndviMean ?? a?.detection?.indices?.ndviMean ?? null,
      gisAlan,
      gisName: s.gisName,
      gisScore: s.gisScore,
      diffPct,
      askiTuketim: s.askiTuketim,
      match: diffPct == null ? 'Bekleniyor' : Math.abs(diffPct) <= 35 ? 'Uyumlu' : 'Sapmalı',
    };
  }), [sites, analyses]);

  const vectorDbRows = useMemo(() => sites.map((s) => {
    const a = analyses[s.aboneno];
    const det = a?.detection;
    const typo = a?.typology;
    const dominantTypo = typo?.typologies?.slice().sort((x, y) => (y.area || 0) - (x.area || 0))[0];
    return {
      aboneno: s.aboneno,
      park_name: s.abone_adi,
      ilce: s.ilce_adi,
      mahalle: s.mahalle_adi,
      geometry: s.coords ? 'Polygon' : null,
      vertex: s.vertexCount,
      lat: s.lat,
      lng: s.lng,
      ndvi_mean: a?.ndvi?.ndviMean ?? det?.indices?.ndviMean ?? null,
      green_m2: det?.totalGreenM2 ?? a?.ndvi?.greenAreaM2 ?? null,
      green_pct: det?.greenPercentage ?? a?.ndvi?.greenRatio ?? null,
      built_m2: det?.classes?.find((c) => /yap/i.test(c.name))?.area ?? null,
      dominant_type: dominantTypo?.name || null,
      gis_alan_m2: s.gisAlan,
      gis_score: s.gisScore,
      db_park_id: s.db_park_id,
      aski_aboneno: s.aboneno,
      status: s.status,
      source_layers: [
        'ASKİ',
        s.coords ? 'Doğrulama/Koordinat' : null,
        s.gisAlan != null ? 'GIS Excel' : null,
        a?.ok ? 'Sentinel-2/GEE' : null,
      ].filter(Boolean).join(' + '),
    };
  }), [sites, analyses]);

  if (loading) {
    return <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>IP3 verileri yükleniyor...</div>;
  }

  return (
    <div style={{ marginTop: 36 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
          İş Paketi 3 — Uydu Görüntüleri ile Yapay Zeka Modülü
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
          3.1 → 3.5 ve Ç.3.1 — gerçek park koordinatları ve (isteğe bağlı) Sentinel-2 / GEE analiz çıktıları
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
        <button
          type="button"
          onClick={initGee}
          disabled={geeStatus === 'loading' || geeStatus === 'ready'}
          style={{
            padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer',
            background: geeStatus === 'ready' ? '#dcfce7' : C.blue, color: geeStatus === 'ready' ? C.green : '#fff',
          }}
        >
          {geeStatus === 'ready' ? 'GEE Hazır' : geeStatus === 'loading' ? 'GEE bağlanıyor...' : '1) GEE Başlat'}
        </button>
        <button
          type="button"
          onClick={runSatelliteAnalyses}
          disabled={running || !sites.some((s) => s.coords)}
          style={{
            padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer',
            background: running ? '#94a3b8' : C.purple, color: '#fff',
          }}
        >
          {running ? 'Uydu analizleri çalışıyor...' : '2) 3.1–3.3 Analizlerini Çalıştır'}
        </button>
        <span style={{ fontSize: 11, color: C.muted }}>
          Koordinatlı park: {sites.filter((s) => s.coords).length}/{sites.length}
        </span>
      </div>
      {geeError && (
        <div style={{ padding: 10, borderRadius: 8, background: '#fef2f2', color: C.red, fontSize: 12, marginBottom: 14 }}>{geeError}</div>
      )}

      {/* 3.1 */}
      <Section step={IP3_STEPS[0]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'Abone', 'İlçe', 'Enlem', 'Boylam', 'Geometri', 'Vertex', 'Kaynak', 'Sensör', 'Çözünürlük', 'BBox'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {extractRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.abone_adi}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.aboneno}</td>
                <td style={td}>{r.ilce_adi}</td>
                <td style={td}>{r.lat != null ? Number(r.lat).toFixed(6) : '-'}</td>
                <td style={td}>{r.lng != null ? Number(r.lng).toFixed(6) : '-'}</td>
                <td style={td}>{r.coords ? 'Polygon' : '-'}</td>
                <td style={td}>{r.vertexCount || '-'}</td>
                <td style={td}>{r.coordSource || '-'}</td>
                <td style={td}>{r.sensor}</td>
                <td style={td}>{r.resolution}</td>
                <td style={{ ...td, fontSize: 10, fontFamily: 'monospace' }}>
                  {r.bbox
                    ? `${r.bbox.minLng.toFixed(4)},${r.bbox.minLat.toFixed(4)} → ${r.bbox.maxLng.toFixed(4)},${r.bbox.maxLat.toFixed(4)}`
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 3.2 */}
      <Section step={IP3_STEPS[1]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'Toplam Alan (m²)', 'Yeşil Alan (m²)', 'Yeşil %', 'Yoğun Bitki', 'Sağlıklı', 'Seyrek', 'Yapılı Alan', 'Su', 'Çıplak Toprak', 'NDVI Ort.', 'Görüntü #'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sites.map((s, i) => {
              const d = analyses[s.aboneno]?.detection;
              const err = analyses[s.aboneno]?.error;
              const cls = (namePart) => d?.classes?.find((c) => c.name.toLowerCase().includes(namePart))?.area;
              return (
                <tr key={s.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                  <td style={{ ...td, fontWeight: 600 }}>{s.abone_adi}</td>
                  <td style={td}>{d ? fmt(d.totalAreaM2, 0) : (err ? 'Hata' : '—')}</td>
                  <td style={{ ...td, fontWeight: 700, color: C.green }}>{d ? fmt(d.totalGreenM2, 0) : '—'}</td>
                  <td style={td}>{d ? `${d.greenPercentage}%` : '—'}</td>
                  <td style={td}>{d ? fmt(cls('yogun'), 0) : '—'}</td>
                  <td style={td}>{d ? fmt(cls('saglikli'), 0) : '—'}</td>
                  <td style={td}>{d ? fmt(cls('seyrek'), 0) : '—'}</td>
                  <td style={td}>{d ? fmt(cls('yap'), 0) : '—'}</td>
                  <td style={td}>{d ? fmt(cls('su'), 0) : '—'}</td>
                  <td style={td}>{d ? fmt(cls('toprak') ?? cls('ciplak'), 0) : '—'}</td>
                  <td style={td}>{d ? Number(d.indices?.ndviMean || 0).toFixed(3) : '—'}</td>
                  <td style={td}>{d?.imageCount ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!Object.keys(analyses).length && (
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Henüz GEE analizi yok. Yukarıdan “Analizlerini Çalıştır” ile doldurulur.</div>
        )}
      </Section>

      {/* 3.3 */}
      <Section step={IP3_STEPS[2]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'Yeşil %', 'Dominant Tip', ...Array.from({ length: 5 }, (_, i) => `Tip ${i + 1}`)].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sites.map((s, i) => {
              const t = analyses[s.aboneno]?.typology;
              const types = (t?.typologies || []).slice().sort((a, b) => (b.area || 0) - (a.area || 0));
              return (
                <tr key={s.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                  <td style={{ ...td, fontWeight: 600 }}>{s.abone_adi}</td>
                  <td style={td}>{t ? `${t.greenPercentage}%` : '—'}</td>
                  <td style={{ ...td, fontWeight: 700, color: C.purple }}>{types[0]?.name || '—'}</td>
                  {[0, 1, 2, 3, 4].map((idx) => (
                    <td key={idx} style={{ ...td, fontSize: 11 }}>
                      {types[idx]
                        ? `${types[idx].name}: ${fmt(types[idx].area, 0)} m² (${types[idx].percentage}%)`
                        : '—'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      {/* 3.4 */}
      <Section step={IP3_STEPS[3]}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Park', 'Uydu Yeşil (m²)', 'Uydu Toplam (m²)', 'NDVI Ort.', 'GIS Alan (m²)', 'GIS Park', 'Eşleşme Skoru', 'Fark %', 'ASKİ Son Tüketim', 'Kontrol'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {crossRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.abone_adi}</td>
                <td style={td}>{fmt(r.satGreen, 0)}</td>
                <td style={td}>{fmt(r.satTotal, 0)}</td>
                <td style={td}>{r.ndviMean != null ? Number(r.ndviMean).toFixed(3) : '-'}</td>
                <td style={td}>{fmt(r.gisAlan, 0)}</td>
                <td style={td}>{r.gisName || '-'}</td>
                <td style={td}>{r.gisScore != null ? `${Math.round(r.gisScore * 100)}%` : '-'}</td>
                <td style={{ ...td, fontWeight: 700, color: r.diffPct == null ? C.muted : Math.abs(r.diffPct) <= 35 ? C.green : C.orange }}>
                  {r.diffPct == null ? '-' : `${fmt(r.diffPct, 1)}%`}
                </td>
                <td style={td}>{fmt(r.askiTuketim, 1)}</td>
                <td style={td}>
                  <span style={{
                    padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                    background: r.match === 'Uyumlu' ? '#dcfce7' : r.match === 'Sapmalı' ? '#ffedd5' : '#f1f5f9',
                    color: r.match === 'Uyumlu' ? C.green : r.match === 'Sapmalı' ? C.orange : C.muted,
                  }}>{r.match}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 3.5 */}
      <Section step={IP3_STEPS[4]}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          Her park için Polygon geometri + öznitelik seti. CBS entegrasyonuna hazır vektör katman şeması.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Abone No', 'Geometri', 'Vertex', 'CRS', 'Katman Adı', 'Öznitelik Sayısı', 'Kaynak Katmanlar', 'Durum'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vectorDbRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontFamily: 'monospace' }}>{r.aboneno}</td>
                <td style={td}>{r.geometry || '-'}</td>
                <td style={td}>{r.vertex || '-'}</td>
                <td style={td}>EPSG:4326 (WGS84)</td>
                <td style={td}>yesil_alan_zenginlestirilmis</td>
                <td style={td}>{Object.keys(r).length}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.source_layers}</td>
                <td style={td}>{r.geometry ? 'Hazır' : 'Koordinat eksik'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Ç.3.1 */}
      <Section step={IP3_STEPS[5]}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          CBS üzerinde entegre edilmiş, öznitelikleri tanımlanmış kentsel yeşil alan veritabanı çıktısı.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['park_name', 'aski_aboneno', 'ilce', 'mahalle', 'lat', 'lng', 'ndvi_mean', 'green_m2', 'green_pct', 'built_m2', 'dominant_type', 'gis_alan_m2', 'gis_score', 'db_park_id', 'status'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vectorDbRows.map((r, i) => (
              <tr key={r.aboneno} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600, maxWidth: 180 }}>{r.park_name}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.aski_aboneno}</td>
                <td style={td}>{r.ilce}</td>
                <td style={td}>{r.mahalle}</td>
                <td style={td}>{r.lat != null ? Number(r.lat).toFixed(5) : '-'}</td>
                <td style={td}>{r.lng != null ? Number(r.lng).toFixed(5) : '-'}</td>
                <td style={td}>{r.ndvi_mean != null ? Number(r.ndvi_mean).toFixed(3) : '-'}</td>
                <td style={td}>{fmt(r.green_m2, 0)}</td>
                <td style={td}>{r.green_pct != null ? `${r.green_pct}%` : '-'}</td>
                <td style={td}>{fmt(r.built_m2, 0)}</td>
                <td style={td}>{r.dominant_type || '-'}</td>
                <td style={td}>{fmt(r.gis_alan_m2, 0)}</td>
                <td style={td}>{r.gis_score != null ? `${Math.round(r.gis_score * 100)}%` : '-'}</td>
                <td style={td}>{r.db_park_id || '-'}</td>
                <td style={td}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default AdminRaporIP3;
